import {
  api,
  clearOperatorSession,
  type components,
  getDeviceToken,
  getRefreshToken,
  setAccessToken,
  setDeviceToken,
  setRefreshToken,
} from "@awtoban/shared";
import * as React from "react";

type SessionUser = components["schemas"]["SessionUser"];

/** unbound → no device token; locked → bound, no operator on shift; active → operator on shift. */
type Phase = "loading" | "unbound" | "locked" | "active";

interface StationInfo {
  id: number;
  code: string;
  name: string;
}

interface AuthContextValue {
  phase: Phase;
  user: SessionUser | null;
  /** Bind this terminal to a station (manager/HQ credentials). */
  bindTerminal: (username: string, password: string, stationCode?: string) => Promise<void>;
  unbindTerminal: () => void;
  /** Start a shift with an operator PIN. Returns an error code on failure. */
  startShift: (operatorId: number, pin: string) => Promise<string | null>;
  endShift: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

const deviceHeaders = () => ({ Authorization: `Bearer ${getDeviceToken() ?? ""}` });

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [user, setUser] = React.useState<SessionUser | null>(null);

  React.useEffect(() => {
    // Access token is in-memory, so a reload always lands on the lock screen
    // when a device is bound — the operator re-enters their PIN.
    setPhase(getDeviceToken() ? "locked" : "unbound");
  }, []);

  const bindTerminal = React.useCallback(
    async (username: string, password: string, stationCode?: string) => {
      const login = await api.POST("/api/v1/auth/login", {
        body: { username, password } as never,
      });
      const tokens = login.data as { access?: string; refresh?: string } | undefined;
      if (login.error || !tokens?.access) throw new Error("login_failed");
      // Temporarily act as the manager so the client attaches their bearer.
      setAccessToken(tokens.access);
      try {
        const bind = await api.POST("/api/v1/device/bind", {
          body: (stationCode ? { station_code: stationCode } : {}) as never,
        });
        if (bind.error || !bind.data) {
          // 403 → not a manager/HQ; 400 → HQ didn't name a (valid) station.
          if (bind.response.status === 403) throw new Error("forbidden");
          if (bind.response.status === 400) throw new Error("station");
          throw new Error("bind_failed");
        }
        const result = bind.data as { device_token: string; station: StationInfo };
        setDeviceToken(result.device_token);
      } finally {
        clearOperatorSession(); // never keep the manager session on the terminal
      }
      setUser(null);
      setPhase("locked");
    },
    [],
  );

  const unbindTerminal = React.useCallback(() => {
    clearOperatorSession();
    setDeviceToken(null);
    setUser(null);
    setPhase("unbound");
  }, []);

  const startShift = React.useCallback(
    async (operatorId: number, pin: string): Promise<string | null> => {
      const { data, error, response } = await api.POST("/api/v1/shift/login", {
        body: { operator_id: operatorId, pin } as never,
        headers: deviceHeaders(),
      });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        return detail ?? (response.status === 401 ? "bad_pin" : "error");
      }
      const tokens = data as { access: string; refresh: string };
      setAccessToken(tokens.access);
      setRefreshToken(tokens.refresh);
      const me = await api.GET("/api/v1/auth/me", {});
      setUser((me.data as SessionUser | undefined) ?? null);
      setPhase("active");
      return null;
    },
    [],
  );

  const endShift = React.useCallback(async () => {
    await api
      .POST("/api/v1/shift/end", { body: { refresh: getRefreshToken() } as never })
      .catch(() => undefined);
    clearOperatorSession();
    setUser(null);
    setPhase("locked");
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ phase, user, bindTerminal, unbindTerminal, startShift, endShift }),
    [phase, user, bindTerminal, unbindTerminal, startShift, endShift],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
